<template>
  <div :class="classes()">
    <Swiper
      ref="swiper"
      :class="`${el}__swiper`"
      @swiper="onSwiper"
      :slides-per-view="1"
      :allow-touch-move="false"
      :auto-height="true"
      :prevent-clicks="false"
      :prevent-clicks-propagation="false"
    >
      <SwiperSlide>
        <form @submit.prevent="handleSubmit">
          <div :class="`${el}__radioButtons`">
            <BaseRadio
              id="gender-w"
              name="gender"
              :label="$t('forms.label.mrs')"
              :class="`${el}__radioButton`"
              :checked="formFields.gender.value === 2"
              @input="setGender(2)"
            />

            <BaseRadio
              id="gender-m"
              name="gender"
              :label="$t('forms.label.mr')"
              :class="`${el}__radioButton`"
              :checked="formFields.gender.value === 1"
              @input="setGender(1)"
            />
          </div>

          <Copy
            v-if="formFields.gender.error"
            element="div"
            size="s"
            :staticClass="`${el}__helperText`"
            color="supportDanger"
          >
            {{ formFields.gender.error }}
          </Copy>

          <Input
            :label="$t('forms.label.firstname')"
            name="firstname"
            v-model.trim="formFields.firstname.value"
            :error="formFields.firstname.error"
            @input="clearError(formFields.firstname)"
            clearable
            hasMargin
          />

          <Input
            :label="$t('forms.label.lastname')"
            name="lastname"
            v-model.trim="formFields.lastname.value"
            :error="formFields.lastname.error"
            @input="clearError(formFields.lastname)"
            clearable
            hasMargin
          />

          <Input
            :label="$t('forms.label.email')"
            type="email"
            name="email"
            v-model.trim="formFields.email.value"
            :error="formFields.email.error"
            @input="clearError(formFields.email)"
            clearable
            hasMargin
          />

          <BaseCheckbox
            id="terms"
            name="privacy"
            v-model="formFields.privacy.value"
            :error="formFields.privacy.error"
            bordered
            @input="clearError(formFields.privacy)"
            hasMargin
          >
            <template slot="label">
              <Copy
                element="div"
                v-for="(item, i) in $t('forms.label.terms.subLabel')"
                :key="`sublabel-${i}`"
                staticClass="u-margin__bottom--1"
              >
                <span v-html="item" />
              </Copy>
            </template>
          </BaseCheckbox>

          <div :class="`${el}__button`">
            <Button
              :loading="loading"
              :label="$t('careLevelCalculator.reducedResult.register.form.cta')"
              class="u-margin__top--3"
              submit
            />
          </div>
        </form>
      </SwiperSlide>
      <SwiperSlide>
        <div :class="`${el}__done`">
          <Icon name="mail-send-fill" color="supportSuccess" :class="`${el}__doneIcon`" />
          <Headline size="s" responsive centered>
            <span v-html="$t('careLevelCalculator.reducedResult.register.done.headline')" />
          </Headline>
          <Copy color="tertiaryLight" size="m" v-html="$t('auth.spam.copy')" />
        </div>
      </SwiperSlide>
    </Swiper>
  </div>
</template>

<script>
import Vue from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import BaseCheckbox from '@/objects/Forms/BaseCheckbox';
import Button from '@/objects/Button';
import BaseRadio from '@/objects/Forms/BaseRadio';
import Icon from '@/objects/Icon';
import Headline from '@/objects/Headline';
import Copy from '@/objects/Copy';
import Input from '@/objects/Forms/BaseInput';

import { emailPattern, namePattern } from '@/helpers/patterns';
import { validateForm } from '@/helpers/formValidation';

import { ROUTE_API_SAVE_UNREGISTERED_USER } from '@/constants/apiRoutes';
import { LS_CARE_LEVEL_CALCULATOR_NEWSLETTER_TOKEN } from '@/constants/storage';
import { trackBIUnregisteredUser } from '@/helpers/biTracking/biTracking';

export default {
  name: 'CareLevelMailForm',
  props: {
    token: String,
  },
  components: {
    Input,
    Copy,
    BaseRadio,
    Headline,
    Icon,
    Button,
    BaseCheckbox,
    Swiper,
    SwiperSlide,
  },
  data() {
    const newsletterToken = localStorage.getItem(LS_CARE_LEVEL_CALCULATOR_NEWSLETTER_TOKEN);

    return {
      sent: false,
      loading: false,
      newsletterToken,
      formDataChanged: true,
      swiperInstance: null,
      formFields: {
        gender: {
          value: '',
          error: null,
          required: this.$t('forms.error.gender.required'),
        },
        firstname: {
          value: '',
          error: null,
          required: this.$t('forms.error.firstname.required'),
          pattern: {
            value: namePattern,
            message: this.$t('forms.error.firstname.formatted'),
          },
        },
        lastname: {
          value: '',
          error: null,
          required: this.$t('forms.error.lastname.required'),
          pattern: {
            value: namePattern,
            message: this.$t('forms.error.lastname.formatted'),
          },
        },
        email: {
          value: '',
          error: null,
          required: this.$t('forms.error.email.generic'),
          pattern: {
            value: emailPattern,
            message: this.$t('forms.error.email.formatted'),
          },
        },
        privacy: {
          value: 0,
          error: null,
          required: this.$t('forms.error.privacy2.required'),
        },
      },
      el: 'c-careLevelMailForm',
      classes: () => {
        const el = this.el;
        return {
          [el]: true,
        };
      },
    };
  },
  created() {
    window.addEventListener('beforeunload', this.beforeWindowUnload);
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.beforeWindowUnload);
  },

  methods: {
    onSwiper(swiper) {
      this.swiperInstance = swiper;
    },

    validateGender() {
      if (this.formFields.gender.value === '') {
        this.formFields.gender.error = this.formFields.gender.required;
        return false;
      } else {
        this.clearError(this.formFields.gender);
        return true;
      }
    },

    setGender(val) {
      this.formFields.gender.value = val;
      this.clearError(this.formFields.gender);
    },

    clearError(field) {
      field.error = null;
    },

    handleSubmit(e) {
      const isGenderValid = this.validateGender();
      const { isValid } = validateForm(e.target.elements, this.formFields);
      if (isValid && isGenderValid) {
        this.sendMail();
      }
    },

    beforeWindowUnload(event) {
      if (this.formDataChanged) {
        // Cancel the event
        event.preventDefault();
        // Chrome requires returnValue to be set
        event.returnValue = '';
      }
    },

    sendMail() {
      this.loading = true;
      this.formDataChanged = false;

      const unregistered = {
        email: this.formFields['email'].value,
        gender: parseInt(this.formFields['gender'].value),
        last_name: this.formFields['lastname'].value,
        first_name: this.formFields['firstname'].value,
        token: this.token,
        newsletter_token: this.newsletterToken,
      };

      Vue.axios
        .post(ROUTE_API_SAVE_UNREGISTERED_USER, { unregistered })
        .then((response) => {
          if (response.status === 200) {
            trackBIUnregisteredUser('pgr_unregistered_result');
            trackBIUnregisteredUser('pgr_newsletter');
            this.loading = false;
            this.sent = true;
            if (this.swiperInstance) {
              this.swiperInstance.slideNext();
            }
            localStorage.removeItem(LS_CARE_LEVEL_CALCULATOR_NEWSLETTER_TOKEN);
            this.$emit('sent');
          }
        })
        .catch((err) => {
          const {
            data: { errors },
            status,
          } = err.response;
          if (status === 422) {
            Object.keys(errors).map((error) => {
              const key = this.map(error);

              const type = errors[error][0];
              this.formFields[key].error = this.$t(`forms.error.${key}.${type}`);
            });
          }
          this.loading = false;
        });
    },

    map(item) {
      return {
        'unregistered.email': 'email',
        'unregistered.first_name': 'firstname',
        'unregistered.last_name': 'lastname',
        'unregistered.gender': 'gender',
      }[item];
    },
  },
};
</script>
